/**
 * KpiDrilldownSheet - Reusable drill-down panel for KPI cards.
 * Opens a side sheet showing the underlying records when a KPI is clicked.
 */
import React, { useState, useEffect } from 'react';
import { useAuth } from '../lib/auth';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '../components/ui/sheet';
import { Badge } from '../components/ui/badge';
import { ScrollArea } from '../components/ui/scroll-area';
import { Loader2, AlertCircle, User, MapPin, Clock, Shield, Flag, Navigation } from 'lucide-react';

const ICON_MAP = {
  on_duty_officers: User,
  active_patrols: Navigation,
  pending_reviews: Flag,
  high_priority_reviews: AlertCircle,
  active_incidents: Shield,
  attendance_anomalies: AlertCircle,
};

function formatTime(iso) {
  if (!iso) return '--';
  return new Date(iso).toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit', hour12: false });
}

function formatDate(iso) {
  if (!iso) return '--';
  return new Date(iso).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
}

function DrilldownItem({ item, kpiType }) {
  if (kpiType === 'on_duty_officers') {
    return (
      <div className="p-3 rounded-lg bg-zinc-900/50 border border-zinc-800 space-y-1" data-testid={`drilldown-item-${item.id}`}>
        <div className="flex items-center justify-between">
          <span className="text-sm font-medium text-white">{item.officer_name}</span>
          <Badge className="text-[10px] bg-emerald-500/20 text-emerald-400">{item.workflow_stage?.replace(/_/g, ' ')}</Badge>
        </div>
        <div className="flex items-center gap-3 text-xs text-zinc-400">
          <span className="flex items-center gap-1"><MapPin className="h-3 w-3" /> {item.site_name}</span>
          <span className="flex items-center gap-1"><Clock className="h-3 w-3" /> {formatTime(item.start_time)}</span>
          {item.shift_type === 'mobile_patrol' && <Badge className="text-[9px] bg-blue-500/20 text-blue-400">Mobile</Badge>}
        </div>
      </div>
    );
  }

  if (kpiType === 'active_patrols') {
    const pct = item.sites_total > 0 ? Math.round((item.sites_completed / item.sites_total) * 100) : 0;
    return (
      <div className="p-3 rounded-lg bg-zinc-900/50 border border-zinc-800 space-y-1" data-testid={`drilldown-item-${item.id}`}>
        <div className="flex items-center justify-between">
          <span className="text-sm font-medium text-white">{item.officer_name}</span>
          <Badge className="text-[10px] bg-blue-500/20 text-blue-400">{pct}%</Badge>
        </div>
        <div className="text-xs text-zinc-400">{item.plan_name} ({item.region})</div>
        <div className="text-xs text-zinc-500">{item.sites_completed}/{item.sites_total} sites | Started: {formatTime(item.started_at)}</div>
      </div>
    );
  }

  if (kpiType === 'pending_reviews' || kpiType === 'high_priority_reviews') {
    return (
      <div className="p-3 rounded-lg bg-zinc-900/50 border border-zinc-800 space-y-1" data-testid={`drilldown-item-${item.id}`}>
        <div className="flex items-center justify-between">
          <span className="text-sm font-medium text-white">{item.officer_name}</span>
          <Badge className={`text-[10px] ${item.priority === 'HIGH' ? 'bg-red-500/20 text-red-400' : item.priority === 'MEDIUM' ? 'bg-amber-500/20 text-amber-400' : 'bg-zinc-500/20 text-zinc-400'}`}>
            {item.priority}
          </Badge>
        </div>
        <div className="text-xs text-zinc-400">{item.plan_name} | {item.completion_percentage}%</div>
        {item.flags?.length > 0 && (
          <div className="flex flex-wrap gap-1 mt-1">
            {item.flags.map((f, i) => <Badge key={i} className="text-[9px] bg-zinc-800 text-zinc-400">{f}</Badge>)}
          </div>
        )}
      </div>
    );
  }

  if (kpiType === 'active_incidents') {
    return (
      <div className="p-3 rounded-lg bg-zinc-900/50 border border-zinc-800 space-y-1" data-testid={`drilldown-item-${item.id}`}>
        <div className="flex items-center justify-between">
          <span className="text-sm font-medium text-white truncate">{item.title}</span>
          <Badge className={`text-[10px] ${item.severity === 'critical' ? 'bg-red-500/20 text-red-400' : item.severity === 'high' ? 'bg-amber-500/20 text-amber-400' : 'bg-zinc-500/20 text-zinc-400'}`}>
            {item.severity || item.status}
          </Badge>
        </div>
        <div className="flex items-center gap-3 text-xs text-zinc-400">
          {item.site_name && <span><MapPin className="h-3 w-3 inline mr-1" />{item.site_name}</span>}
          <span>{formatDate(item.created_at)}</span>
        </div>
      </div>
    );
  }

  if (kpiType === 'attendance_anomalies') {
    return (
      <div className="p-3 rounded-lg bg-zinc-900/50 border border-zinc-800 space-y-1" data-testid={`drilldown-item-${item.id}`}>
        <div className="flex items-center justify-between">
          <span className="text-sm font-medium text-white">{item.officer_name}</span>
          <Badge className="text-[10px] bg-amber-500/20 text-amber-400">{item.anomaly_type}</Badge>
        </div>
        <div className="text-xs text-zinc-400">{item.description || item.site_name}</div>
      </div>
    );
  }

  // Generic fallback
  return (
    <div className="p-3 rounded-lg bg-zinc-900/50 border border-zinc-800 text-sm text-zinc-300" data-testid={`drilldown-item-${item.id}`}>
      {JSON.stringify(item).slice(0, 200)}
    </div>
  );
}

export default function KpiDrilldownSheet({ open, onClose, kpiType, title, kpiValue }) {
  const { api } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const Icon = ICON_MAP[kpiType] || Shield;

  useEffect(() => {
    if (!open || !kpiType) return;
    let cancelled = false;
    setLoading(true);
    setError(null);

    api.get(`/executive/kpi-drilldown/${kpiType}`)
      .then(res => {
        if (!cancelled) setData(res.data);
      })
      .catch(err => {
        if (!cancelled) setError(err.response?.data?.detail || 'Failed to load data');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => { cancelled = true; };
  }, [open, kpiType, api]);

  return (
    <Sheet open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <SheetContent side="right" className="w-[400px] sm:w-[480px] bg-[#09090b] border-l border-zinc-800 p-0">
        <SheetHeader className="p-4 border-b border-zinc-800">
          <SheetTitle className="flex items-center gap-2 text-white">
            <Icon className="h-5 w-5 text-blue-400" />
            {title || data?.title || 'KPI Details'}
          </SheetTitle>
          <SheetDescription className="text-zinc-400">
            {data?.count != null ? `${data.count} record${data.count !== 1 ? 's' : ''}` : ''}
            {kpiValue != null && data?.count != null && data.count !== kpiValue && (
              <span className="text-amber-400 ml-2">(KPI shows {kpiValue})</span>
            )}
          </SheetDescription>
        </SheetHeader>

        <ScrollArea className="h-[calc(100vh-100px)]">
          <div className="p-4 space-y-2">
            {loading && (
              <div className="flex items-center justify-center py-12" data-testid="drilldown-loading">
                <Loader2 className="h-6 w-6 animate-spin text-zinc-500" />
              </div>
            )}

            {error && (
              <div className="flex flex-col items-center py-12 text-center space-y-2" data-testid="drilldown-error">
                <AlertCircle className="h-8 w-8 text-red-500" />
                <p className="text-sm text-red-400">{error}</p>
              </div>
            )}

            {!loading && !error && data?.items?.length === 0 && (
              <div className="flex flex-col items-center py-12 text-center space-y-2" data-testid="drilldown-empty">
                <Icon className="h-8 w-8 text-zinc-600" />
                <p className="text-sm text-zinc-500">No records found</p>
              </div>
            )}

            {!loading && !error && data?.items?.map((item) => (
              <DrilldownItem key={item.id} item={item} kpiType={kpiType} />
            ))}
          </div>
        </ScrollArea>
      </SheetContent>
    </Sheet>
  );
}
